import { css, html } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { until } from 'lit/directives/until.js'

import { OxGristEditor } from './ox-grist-editor.js'
import { SelectOption, SelectOptionObject } from '../types.js'

function toOptionObjects(options: SelectOption[]): SelectOptionObject[] {
  return options.map(option => {
    switch (typeof option) {
      case 'string':
        return { display: option, value: option }
      case 'object':
        return { display: option.display || option.name, value: option.value }
      default:
        return option
    }
  }) as SelectOptionObject[]
}

@customElement('ox-grist-editor-text-select')
export class OxGristEditorTextSelect extends OxGristEditor {
  static styles = [
    ...([OxGristEditor.styles].flat()),
    css`
      input {
        flex: 1;
        width: 100%;
        height: 100%;
        border: 0;
        background-color: transparent;
        box-sizing: border-box;
        color: var(--md-sys-color-on-background);
      }

      input:focus {
        outline: none;
      }
    `
  ]

  @state() private _showDropdown: boolean = false
  @state() private _resolvedOptions: SelectOptionObject[] = []

  private _dropdownEl: HTMLDivElement | null = null

  get inlineEditable() {
    return true
  }

  get editorTemplate() {
    var rowOptionField = this.record[this.column.record.rowOptionField || '']
    var { options = [] } = rowOptionField ? rowOptionField : this.column.record

    if (typeof options == 'function') {
      options = options.call(null, this.value, this.column, this.record, this.rowIndex, this.field)

      if (options instanceof Promise) {
        return html`${until(
          options.then(opts => {
            this._resolvedOptions = toOptionObjects((opts || []) as SelectOption[])
            return this._buildTemplate()
          })
        )}`
      } else {
        this._resolvedOptions = toOptionObjects((options || []) as SelectOption[])
        return this._buildTemplate()
      }
    } else {
      this._resolvedOptions = toOptionObjects((options || []) as SelectOption[])
      return this._buildTemplate()
    }
  }

  private _buildTemplate() {
    return html`<input
      type="text"
      .value=${this.value == null ? '' : this.value}
      @focus=${this._onInputFocus}
      @input=${this._onInputChange}
    />`
  }

  private _onInputFocus() {
    if (this._resolvedOptions.length > 0) {
      this._showFloatingDropdown()
    }
  }

  private _onInputChange(e: Event) {
    const value = (e.target as HTMLInputElement).value
    this._dirtyValue = this.formatFromEditor(value)
  }

  private _showFloatingDropdown() {
    this._removeFloatingDropdown()

    const rect = this.getBoundingClientRect()

    const dropdown = document.createElement('div')
    dropdown.style.cssText = `
      position: fixed;
      top: ${rect.bottom}px;
      left: ${rect.left}px;
      width: ${rect.width * 2}px;
      background: #fff;
      border: 1px solid #ccc;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      z-index: 99999;
      max-height: 200px;
      overflow-y: auto;
      border-radius: 0 0 4px 4px;
    `

    this._resolvedOptions.forEach(opt => {
      const item = document.createElement('div')
      item.textContent = String(opt.display || opt.value)
      item.style.cssText = `
        padding: 8px 12px;
        cursor: pointer;
        font-size: 13px;
        border-bottom: 1px solid #eee;
      `
      item.addEventListener('mouseenter', () => {
        item.style.backgroundColor = '#e3f2fd'
      })
      item.addEventListener('mouseleave', () => {
        item.style.backgroundColor = '#fff'
      })
      item.addEventListener('pointerdown', (e: Event) => {
        e.preventDefault()
        e.stopPropagation()
        this._selectOption(opt)
      })
      dropdown.appendChild(item)
    })

    document.body.appendChild(dropdown)
    this._dropdownEl = dropdown
  }

  private _selectOption(opt: SelectOptionObject) {
    this._removeFloatingDropdown()

    const newValue = this.formatFromEditor(opt.value)
    const { name = '' } = this.column
    const currentValue = this.record[name] || ''

    // input 값 갱신
    const input = this.renderRoot.querySelector('input') as HTMLInputElement
    if (input) {
      input.value = String(opt.value)
    }

    // field-change 직접 dispatch
    this._dirtyValue = newValue
    this.dispatchEvent(
      new CustomEvent('field-change', {
        bubbles: true,
        composed: true,
        detail: {
          before: currentValue,
          after: newValue,
          column: this.column,
          record: this.record,
          row: this.row
        }
      })
    )
  }

  private _removeFloatingDropdown() {
    if (this._dropdownEl) {
      this._dropdownEl.remove()
      this._dropdownEl = null
    }
  }

  override _onfocusout() {
    setTimeout(() => {
      this._removeFloatingDropdown()
      super._onfocusout()
    }, 150)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this._removeFloatingDropdown()
  }
}
